import { V2_FILE_UPLOAD_POLICY } from './types';

// Prepares a picked photo for v2 staging. A modern phone camera produces a
// 12MP HEIC/JPEG well over the 10MB policy cap; instead of telling the patient
// "file is too large", decode it here, cap the longest edge, and stage a JPEG.
// The server contract is untouched — staged bytes are always png/jpeg/webp.
//
// Pass-through: an already-small png/jpeg/webp uploads byte-identical to
// today's behavior (and to the e2e fixtures). HEIC/HEIF always transcode; a
// browser that cannot decode them (no HEIC support outside Safari) surfaces
// the policy's "Invalid file type" copy through FileUploadField's error path.
//
// A field that pins its own `accepted_file_types` (the standard card fields
// accept png/jpeg only) is checked again at submit, so a file of a type it does
// not list is never passed through: it transcodes like HEIC, to JPEG, or to PNG
// when the field takes PNG but not JPEG (booking-frontend#50).

const MAX_EDGE = 2048;
const PASS_THROUGH_MAX_BYTES = 2 * 1024 * 1024;
const PASS_THROUGH_MAX_EDGE = 2000;
const PASS_THROUGH_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const JPEG_QUALITY = 0.85;

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file);
  } catch {
    // <img> fallback — some engines decode more via the media pipeline.
    const url = URL.createObjectURL(file);
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('undecodable'));
        img.src = url;
      });
    } finally {
      // Safe once load/error has fired; the bitmap is already decoded.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  }
}

function dimensions(img: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  return 'naturalWidth' in img
    ? { width: img.naturalWidth, height: img.naturalHeight }
    : { width: img.width, height: img.height };
}

/** The field's pinned types, normalised as the backend reads them; null when it pins none. */
function pinnedTypes(accepted: readonly string[] | null | undefined): string[] | null {
  const types = (accepted ?? []).map((t) => t.trim().toLowerCase()).filter((t) => t !== '');
  return types.length > 0 ? types : null;
}

export async function prepareUploadFile(file: File, acceptedFileTypes?: readonly string[] | null): Promise<File> {
  const accepted = pinnedTypes(acceptedFileTypes);
  const fieldTakes = (type: string) => !accepted || accepted.indexOf(type) !== -1;
  const smallEnough = file.size <= PASS_THROUGH_MAX_BYTES;
  const passThroughType = PASS_THROUGH_TYPES.indexOf(file.type) !== -1 && fieldTakes(file.type);
  const outType = fieldTakes('image/jpeg') || !fieldTakes('image/png') ? 'image/jpeg' : 'image/png';

  let img: ImageBitmap | HTMLImageElement;
  try {
    img = await decode(file);
  } catch {
    if (passThroughType) return file; // undecodable but already an accepted type: let the server judge it
    throw new Error(V2_FILE_UPLOAD_POLICY.typeError);
  }

  try {
    const { width, height } = dimensions(img);
    if (!width || !height) {
      if (passThroughType) return file;
      throw new Error(V2_FILE_UPLOAD_POLICY.typeError);
    }
    const maxEdge = Math.max(width, height);
    if (passThroughType && smallEnough && maxEdge <= PASS_THROUGH_MAX_EDGE) return file;

    const scale = Math.min(1, MAX_EDGE / maxEdge);
    const outW = Math.max(1, Math.round(width * scale));
    const outH = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return passThroughType ? file : Promise.reject(new Error(V2_FILE_UPLOAD_POLICY.typeError));
    // Card/ID photos read best on a solid ground; JPEG has no alpha anyway.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, outType, JPEG_QUALITY));
    if (!blob) {
      if (passThroughType) return file;
      throw new Error(V2_FILE_UPLOAD_POLICY.typeError);
    }
    const baseName = (file.name || 'photo').replace(/\.[A-Za-z0-9]+$/, '');
    return new File([blob], `${baseName}.${outType === 'image/png' ? 'png' : 'jpg'}`, { type: outType });
  } finally {
    if ('close' in img) {
      try { img.close(); } catch { /* already closed */ }
    }
  }
}
